import {
    languageCodeZodSchema,
    type LanguageCode,
} from "@@/shared/translations/language-code";
import type { Except } from "type-fest";
import { z } from "zod";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
    type IsoAndUnixTimestamp,
} from "../date-time/iso-and-unix";
import { unitTypeMap, unitTypeZodSchema, type UnitType } from "./unit-types";

const wordTypes = ["singular", "plural", "short", "shortPlural"] as const;
type WordType = (typeof wordTypes)[number];
const wordTypeZodSchema = z.enum(wordTypes);

type UnitNameValue = {
    [nameType in Exclude<WordType, "shortPlural">]: string;
} & { [nameType in Extract<WordType, "shortPlural">]?: string };

const unitNameValueZodSchema: z.ZodSchema<UnitNameValue> = z.object({
    singular: z.string(),
    plural: z.string(),
    short: z.string(),
    shortPlural: z.string().optional(),
});

type UnitName = Except<
    Partial<Record<LanguageCode, UnitNameValue>>,
    "sv" | "en"
> & {
    sv: UnitNameValue;
    en: UnitNameValue;
    de?: UnitNameValue;
};
//  {
//     [languageCode in LanguageCode]: UnitNameValue;
// };

const unitNameZodSchema: z.ZodSchema<UnitName> = z.record(
    languageCodeZodSchema,
    unitNameValueZodSchema,
) as z.ZodSchema<UnitName>; // quickfix zod issue,;

type Unit = {
    _id: string;
    _type: "UNIT";
    _createdAt: IsoAndUnixTimestamp;
    type: UnitType;
    name: UnitName;
};

const unitZodSchema: z.ZodSchema<Unit> = z.object({
    _id: z.string(),
    _type: z.literal("UNIT"),
    _createdAt: isoAndUnixZodSchema,
    type: unitTypeZodSchema,
    name: unitNameZodSchema,
});

const units: { [unitType in UnitType]: Unit } = {
    night: {
        _id: "unit_night",
        _type: "UNIT",
        _createdAt: nowIsoAndUnix(),
        type: unitTypeMap.night,
        name: {
            sv: {
                plural: "nätter",
                short: "natt",
                shortPlural: "nätter",
                singular: "natt",
            },
            en: {
                plural: "nights",
                short: "night",
                shortPlural: "nätter",
                singular: "night",
            },
            de: {
                plural: "Nächte",
                short: "Nacht",
                shortPlural: "Nächte",
                singular: "Nacht",
            },
        },
    },
    minute: {
        _id: "unit_minute",
        _type: "UNIT",
        _createdAt: nowIsoAndUnix(),
        type: unitTypeMap.minute,
        name: {
            sv: {
                plural: "minuter",
                short: "min",
                singular: "minut",
            },
            en: {
                plural: "minutes",
                short: "min",
                singular: "minute",
            },
        },
    },
    millisecond: {
        _id: "unit_millisecond",
        _type: "UNIT",
        _createdAt: nowIsoAndUnix(),
        type: unitTypeMap.millisecond,
        name: {
            sv: {
                plural: "millisekunder",
                short: "ms",
                singular: "millisekund",
            },
            en: {
                plural: "milliseconds",
                short: "ms",
                singular: "millisecond",
            },
        },
    },
    second: {
        _id: "unit_second",
        _type: "UNIT",
        _createdAt: nowIsoAndUnix(),
        type: unitTypeMap.second,
        name: {
            sv: {
                plural: "sekunder",
                short: "s",
                singular: "sekund",
            },
            en: {
                plural: "seconds",
                short: "s",
                singular: "second",
            },
        },
    },
    pcs: {
        _id: `unit_pcs`,
        _type: "UNIT",
        _createdAt: nowIsoAndUnix(),
        type: unitTypeMap.pcs,
        name: {
            sv: {
                plural: "stycken",
                singular: "styck",
                short: "st",
            },
            en: {
                plural: "pieces",
                singular: "piece",
                short: "pc",
                shortPlural: "pcs",
            },
        },
    },
    cl: {
        _id: `unit_cl`,
        _type: "UNIT",
        _createdAt: nowIsoAndUnix(),
        type: unitTypeMap.cl,
        name: {
            sv: {
                plural: "centiliter",
                singular: "centiliter",
                short: "cl",
            },
            en: {
                plural: "centiliter",
                singular: "centiliter",
                short: "cl",
            },
        },
    },
    glass: {
        _id: `unit_glass`,
        _type: "UNIT",
        _createdAt: nowIsoAndUnix(),
        type: unitTypeMap.glass,
        name: {
            sv: {
                plural: "glas",
                singular: "glas",
                short: "gl",
            },
            en: {
                plural: "glass",
                singular: "glass",
                short: "gl",
            },
        },
    },
    bottle: {
        _id: `unit_bottle`,
        _type: "UNIT",
        _createdAt: nowIsoAndUnix(),
        type: unitTypeMap.bottle,
        name: {
            sv: {
                plural: "flaskor",
                singular: "flaska",
                short: "fl",
            },
            en: {
                plural: "bottles",
                singular: "bottle",
                short: "btl",
            },
        },
    },
    portion: {
        _id: `unit_portion`,
        _type: "UNIT",
        _createdAt: nowIsoAndUnix(),
        type: unitTypeMap.portion,
        name: {
            sv: {
                plural: "portioner",
                singular: "portion",
                short: "port",
            },
            en: {
                plural: "portions",
                singular: "portion",
                short: "ptn",
            },
        },
    },
    hour: {
        _id: `unit_hour`,
        _type: "UNIT",
        _createdAt: nowIsoAndUnix(),
        type: unitTypeMap.hour,
        name: {
            sv: {
                plural: "timmar",
                singular: "timme",
                short: "h",
            },
            en: {
                plural: "hours",
                singular: "hour",
                short: "h",
            },
        },
    },
    day: {
        _id: `unit_day`,
        _type: "UNIT",
        _createdAt: nowIsoAndUnix(),
        type: unitTypeMap.day,
        name: {
            sv: {
                plural: "dagar",
                singular: "dag",
                short: "dag",
                shortPlural: "dagar",
            },
            en: {
                plural: "days",
                singular: "day",
                short: "day",
                shortPlural: "days",
            },
        },
    },
    morning: {
        _id: `unit_morning`,
        _type: "UNIT",
        _createdAt: nowIsoAndUnix(),
        type: unitTypeMap.morning,
        name: {
            sv: {
                plural: "förmiddagar",
                singular: "förmiddag",
                short: "fm",
            },
            en: {
                plural: "mornings",
                singular: "morning",
                short: "am",
            },
        },
    },
    afternoon: {
        _id: `unit_afternoon`,
        _type: "UNIT",
        _createdAt: nowIsoAndUnix(),
        type: unitTypeMap.afternoon,
        name: {
            sv: {
                plural: "förmiddagar",
                singular: "förmiddag",
                short: "fm",
            },
            en: {
                plural: "afternoons",
                singular: "afternoon",
                short: "pm",
            },
        },
    },
    ticket: {
        _id: `unit_ticket`,
        _type: "UNIT",
        _createdAt: nowIsoAndUnix(),
        type: unitTypeMap.ticket,
        name: {
            sv: {
                plural: "biljetter",
                singular: "biljett",
                short: "bilj",
            },
            en: {
                plural: "tickets",
                singular: "ticket",
                short: "tkt",
            },
        },
    },
    participant: {
        _id: `unit_participant`,
        _type: "UNIT",
        _createdAt: nowIsoAndUnix(),
        type: unitTypeMap.participant,
        name: {
            sv: {
                plural: "deltagare",
                singular: "deltagare",
                short: "?",
            },
            en: {
                plural: "participants",
                singular: "participant",
                short: "?",
            },
        },
    },
    accommodation: {
        _id: `unit_accommodation`,
        _type: "UNIT",
        _createdAt: nowIsoAndUnix(),
        type: unitTypeMap.accommodation,
        name: {
            sv: {
                plural: "boenden",
                singular: "boende",
                short: "?",
            },
            en: {
                plural: "accommodations",
                singular: "accommodation",
                short: "?",
            },
        },
    },
};

export {
    wordTypes as nounTypes,
    wordTypeZodSchema as nounTypeZodSchema,
    units,
    unitZodSchema,
};
export type { WordType as NounType, Unit };
